// utils/device.js

/**
 * Detects if the current device is a mobile device
 * @returns {boolean} True if mobile device
 */
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Detects if the current device has touch capabilities
 * @returns {boolean} True if touch device
 */
export const isTouchDevice = () => {
  return 'ontouchstart' in window || 
         navigator.maxTouchPoints > 0 || 
         (navigator.msMaxTouchPoints && navigator.msMaxTouchPoints > 0);
};

export function getScreenShareSupport() {
  const ua = navigator.userAgent;
  const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isWebView = /wv/.test(ua.toLowerCase());
  const isBadBrowser = /vivobrowser|miuibrowser|huawei|samsungbrowser|facebook|instagram|line|opera mini|crios|fbav|snapchat|linkedin/i.test(ua);

  const isPWA = window.matchMedia('(display-mode: standalone)').matches;
  const isChrome = /Chrome/.test(ua) && !/Edg|OPR|SamsungBrowser/.test(ua);
  const chromeVersionMatch = ua.match(/Chrome\/(\d+)/);
  const chromeVersion = chromeVersionMatch ? parseInt(chromeVersionMatch[1]) : 0;

  if (isIOS) {
    return {
      supported: true,
      canShare: false,
      iOS: true,
      pwa: isPWA,
      message: "iOS fallback available"
    };
  }

  if (isBadBrowser || isWebView || (isAndroid && (!isChrome || chromeVersion < 81))) {
    return {
      supported: false,
      canShare: false,
      iOS: false,
      message: "Unsupported browser. Open in latest Google Chrome"
    };
  }

  const canShare = !!navigator.mediaDevices?.getDisplayMedia;

  return {
    supported: canShare,
    canShare,
    iOS: false,
    message: canShare ? null : "Your browser doesn't support screen sharing"
  };
}




// Helper function to get browser version
function getBrowserVersion(browserName) {
  const ua = navigator.userAgent;
  const regex = new RegExp(`${browserName}/(\\d+)`);
  const match = ua.match(regex);
  return match ? parseInt(match[1]) : 0;
}

// Simple boolean check
export const isScreenShareSupported = () => {
  return getScreenShareSupport().supported;
};



// Optimized constraints for mobile
export const getScreenShareConstraints = () => {
  const isMobile = isMobileDevice();
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

  const baseConstraints = {
    video: {
      width: { ideal: isMobile ? 1280 : 1920 },
      height: { ideal: isMobile ? 720 : 1080 },
      frameRate: { ideal: isMobile ? 15 : 30 }
    },
    audio: false
  };

  if (isIOS) {
    return {
      video: {
        ...baseConstraints.video,
        mediaSource: 'screen',
        cursor: 'never'
      },
      audio: false
    };
  }

  return {
    ...baseConstraints,
    video: {
      ...baseConstraints.video,
      displaySurface: 'monitor',
      logicalSurface: true
    }
  };
};

/**
 * Gets detailed screen sharing support information
 * @returns {Object} Support details object
 */
export const supportsScreenSharing = () => {
  return {
    supported: isScreenShareSupported(),
    mobile: isMobileDevice(),
    iOS: /iphone|ipad|ipod/i.test(navigator.userAgent.toLowerCase()),
    pwa: window.matchMedia('(display-mode: standalone)').matches,
    userAgent: navigator.userAgent,
    supportInfo: getDetailedSupportInfo()
  };
};

// Internal helper function
function getDetailedSupportInfo() {
  const ua = navigator.userAgent.toLowerCase();
  const versionMatch = ua.match(/(chrome|firefox|samsungbrowser|version)\/(\d+)/);
  
  return {
    browser: detectBrowserName(),
    version: versionMatch ? parseInt(versionMatch[2]) : 0,
    getDisplayMedia: !!navigator.mediaDevices?.getDisplayMedia,
    touchSupport: isTouchDevice()
  };
}

function detectBrowserName() {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('chrome')) return 'chrome';
  if (ua.includes('firefox')) return 'firefox';
  if (ua.includes('samsungbrowser')) return 'samsung';
  if (ua.includes('safari')) return 'safari';
  if (ua.includes('edge')) return 'edge';
  if (ua.includes('opera')) return 'opera';
  return 'unknown';
}